import EventIcon from "@mui/icons-material/Event";
import LinkIcon from "@mui/icons-material/Link";
import PersonIcon from "@mui/icons-material/Person";
import SummarizeIcon from "@mui/icons-material/Summarize";
import CopyMenuButton, {CopyMenuEntry} from "crqlar-react-common/dist/components/CopyMenuButton";
import {Reservation, ReservationSource} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import {getProfileFullName} from "crqlar-react-common/dist/utils/profile.util";
import {useProfilesQuery} from "crqlar-react-common/dist/utils/hooks/profile/useProfiles";
import {useResources} from "crqlar-react-common/dist/utils/hooks/resources/useResources";
import {useAllSectors} from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {useAllServices} from "crqlar-react-common/dist/utils/hooks/services/useServices";
import {DateTime} from "luxon";
import {useTranslation} from "react-i18next";

interface Props {
    reservation: Reservation
}

const externalIds = (reservation: Reservation) =>
    (reservation.externalInfo ?? []).map(info => `${info.provider} ${info.id}`).join(', ')

/**
    Copy for the reservation details header: a plain-text summary of the booking and its guests for a colleague's
    email or chat (or a support ticket), the reservation and lead guest ids, and the booking's System link.
*/
const ReservationCopyButton = ({reservation}: Props) => {

    const {t} = useTranslation()
    const organization = useOrganization()
    const sectors = useAllSectors()
    const services = useAllServices()
    // Same queries as the guest sidebar and the resource picker, so they come from the cache.
    const {data: guests} = useProfilesQuery(reservation.profileIds ?? [])
    const resources = useResources(reservation.resourceIds ?? [])

    const leadGuestId = reservation.primaryProfileId ?? reservation.profileIds?.[0]
    const link = `${window.location.origin}/system/reservations/${reservation.id}`

    const summary = () => {
        const start = reservation.expectedStartDate && DateTime.fromJSDate(new Date(reservation.expectedStartDate))
        const source = {
            [ReservationSource.GUEST_APP]: t('Guest app'),
            [ReservationSource.BOOKING_ENGINE]: t('Booking engine'),
            [ReservationSource.DASHBOARD]: t('Dashboard'),
            [ReservationSource.PMS]: 'PMS',
            [ReservationSource.SYSTEM]: undefined,
        }[reservation.source ?? ReservationSource.SYSTEM]

        const lines: [string, string | undefined][] = [
            [t('Organization'), organization && [organization.name, organization.id && `(${organization.id})`].filter(Boolean).join(' ')],
            [t('Sector'), sectors.find(sector => sector.id === reservation.sectorId)?.name],
            [t('Booking'), [
                services.find(service => service.id === reservation.serviceId)?.name,
                start && start.isValid ? start.toFormat('dd.MM.yyyy HH:mm') : undefined,
                resources.map(resource => resource.name).filter(Boolean).join(', '),
            ].filter(Boolean).join(' · ')],
            [t('Reservation'), [reservation.id, reservation.code && `${t('Code')} ${reservation.code}`, source].filter(Boolean).join(' · ')],
            [t('PMS reservation'), externalIds(reservation)],
            // Lead guest first, the way the sidebar lists them.
            ...[...(guests ?? [])]
                .sort((a, b) => Number(b.id === leadGuestId) - Number(a.id === leadGuestId))
                .map(guest => [t('Guest'), `${getProfileFullName(guest)} · ${guest.id}`] as [string, string]),
            [t('Link'), link],
            [t('Page'), window.location.href],
        ]
        return lines.filter(([, value]) => !!value).map(([label, value]) => `${label}: ${value}`).join('\n')
    }

    const entries = [
        {label: t('Copy summary'), Icon: SummarizeIcon, text: summary},
        {label: t('Copy reservation ID'), Icon: EventIcon, text: () => reservation.id!},
        leadGuestId && {label: t('Copy profile ID'), Icon: PersonIcon, text: () => leadGuestId},
        {label: t('Copy link'), Icon: LinkIcon, text: () => link},
    ].filter(Boolean) as CopyMenuEntry[]

    return <CopyMenuButton entries={entries}/>
}

export default ReservationCopyButton;
